import React, { useState } from 'react';
import { Table, Button, Form, Modal, Spinner, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../shared/api';
import { useConfirm } from '../../shared/ConfirmContext';
import { IconPlus } from '@tabler/icons-react';
import PageShell from '../../components/layout/PageShell';
import PageSection from '../../components/layout/PageSection';
import AppButton from '../../components/ui/AppButton';

const ProjectTaskStatuses = () => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3b82f6'
  });

  const { data: taskStatuses = [], isLoading } = useQuery({
    queryKey: ['project-task-statuses'],
    queryFn: () => api.get('projects/api/project-task-statuses/').then(d => Array.isArray(d) ? d : (d.results || [])),
  });

  const saveMutation = useMutation({
    mutationFn: (payload) => editingStatus
      ? api.put(`projects/api/project-task-statuses/${editingStatus.id}/`, payload)
      : api.post('projects/api/project-task-statuses/', payload),
    onSuccess: () => {
      setSuccess(editingStatus ? t('settings.projectTaskStatuses.success.updated') : t('settings.projectTaskStatuses.success.created'));
      setShowModal(false);
      queryClient.invalidateQueries({ queryKey: ['project-task-statuses'] });
    },
    onError: (err) => setError(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (statusId) => api.delete(`projects/api/project-task-statuses/${statusId}/`),
    onSuccess: () => {
      setSuccess(t('settings.projectTaskStatuses.success.deleted'));
      queryClient.invalidateQueries({ queryKey: ['project-task-statuses'] });
    },
    onError: (err) => setError(err.message),
  });

  const loading = isLoading || saveMutation.isPending || deleteMutation.isPending;

  const handleAdd = () => {
    setEditingStatus(null);
    setFormData({ name: '', description: '', color: '#3b82f6' });
    setError(null);
    setShowModal(true);
  };

  const handleEdit = (status) => {
    setEditingStatus(status);
    setFormData({
      name: status.name,
      description: status.description || '',
      color: status.color || '#3b82f6'
    });
    setError(null);
    setShowModal(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    saveMutation.mutate(formData);
  };

  const handleDelete = async (statusId) => {
    if (!await confirm(t('settings.projectTaskStatuses.deleteConfirm'))) return;
    setError(null);
    setSuccess(null);
    deleteMutation.mutate(statusId);
  };

  return (
    <PageShell
      pretitle={t('settings.overview')}
      title={t('settings.projectTaskStatuses.title')}
      actions={
        <AppButton variant="primary" icon={<IconPlus size={16} />} onClick={handleAdd}>
          {t('settings.projectTaskStatuses.add')}
        </AppButton>
      }
    >
      {error && <div className="alert alert-danger app-inline-alert" role="alert">{error}</div>}
      {success && <div className="alert alert-success app-inline-alert" role="alert">{success}</div>}

      <PageSection
        title={t('settings.projectTaskStatuses.title')}
        description={t('settings.projectTaskStatuses.subtitle')}
        bodyClassName="p-0"
      >
        {loading && taskStatuses.length === 0 ? (
          <div className="text-center p-4">
            <Spinner animation="border" size="sm" className="me-2" />
            {t('common.loading')}
          </div>
        ) : (
          <Table responsive hover className="mb-0">
            <thead>
              <tr>
                <th>{t('settings.projectTaskStatuses.name')}</th>
                <th>{t('settings.projectTaskStatuses.color')}</th>
                <th>{t('settings.projectTaskStatuses.description')}</th>
                <th className="text-end">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {taskStatuses.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center text-muted py-4">
                    {t('settings.projectTaskStatuses.noStatuses')}
                  </td>
                </tr>
              ) : taskStatuses.map(status => (
                <tr key={status.id}>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      <span
                        style={{
                          display: 'inline-block',
                          width: 14,
                          height: 14,
                          borderRadius: 3,
                          backgroundColor: status.color || '#ccc',
                          flexShrink: 0
                        }}
                      />
                      <strong>{status.name}</strong>
                    </div>
                  </td>
                  <td>
                    <code>{status.color}</code>
                  </td>
                  <td className="text-muted">{status.description || '—'}</td>
                  <td className="text-end">
                    <div className="btn-group">
                      <Button
                        size="sm"
                        variant="outline-info"
                        onClick={() => handleEdit(status)}
                        disabled={loading}
                        title={t('common.edit')}
                      >
                        <i className="ti ti-edit"></i>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => handleDelete(status.id)}
                        disabled={loading}
                        title={t('common.delete')}
                      >
                        <i className="ti ti-trash"></i>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </PageSection>

      {/* Add / Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              {editingStatus ? t('settings.projectTaskStatuses.edit') : t('settings.projectTaskStatuses.add')}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {error && (
              <Alert variant="danger" dismissible onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            <Form.Group className="mb-3">
              <Form.Label>{t('settings.projectTaskStatuses.name')} *</Form.Label>
              <Form.Control
                type="text"
                required
                value={formData.name}
                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder={t('settings.projectTaskStatuses.namePlaceholder')}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('settings.projectTaskStatuses.color')}</Form.Label>
              <div className="d-flex align-items-center gap-2">
                <Form.Control
                  type="color"
                  value={formData.color}
                  onChange={e => setFormData(prev => ({ ...prev, color: e.target.value }))}
                  style={{ width: 48, height: 38, padding: 2, cursor: 'pointer' }}
                />
                <Form.Control
                  type="text"
                  value={formData.color}
                  onChange={e => setFormData(prev => ({ ...prev, color: e.target.value }))}
                  placeholder="#3b82f6"
                  pattern="^#[0-9A-Fa-f]{6}$"
                />
              </div>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('settings.projectTaskStatuses.description')}</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.description}
                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder={t('settings.projectTaskStatuses.descriptionPlaceholder')}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              {t('common.cancel')}
            </Button>
            <AppButton type="submit" variant="primary" loading={loading} loadingText={t('common.saving')}>
              {editingStatus ? t('common.save') : t('settings.projectTaskStatuses.add')}
            </AppButton>
          </Modal.Footer>
        </Form>
      </Modal>
    </PageShell>
  );
};

export default ProjectTaskStatuses;
